import { isFreeMonthsOfferEligible } from "@utils/userSegment/features";
import { Plan } from "@utils/redux/slices/plans";
import { AppDispatch, GetAppState } from "@utils/redux/internal";
import { trackTask } from "@utils/monitoring/events/tasks";
import { setRecommendPlanById } from "@utils/redux/slices/recommendedPlan";
import { getURLParams } from "@utils/urlParams";
import { applyPromoCode } from "@utils/api/purchase/checkout";
import { getSessionState, updateSessionState } from "@pageDefinitions/session";
import { fetchProductPlanAsPlanById } from "@utils/plans";
import { clearPromoCode, setPromoCode } from "@utils/redux/slices/promoCode";
import { captureException } from "@utils/error";

type FreeMonthsOfferConfiguration = {
  shorterPlanID: string;
  longerPlanID: string;
  discountPercentage: number;
};

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    freeMonthsOfferConfiguration?: FreeMonthsOfferConfiguration;
  }
}

export function loadFreeMonthsOfferConfiguration() {
  const { shorter_plan_id, noom_plan_id, discount_percentage } = getURLParams();
  if (shorter_plan_id && noom_plan_id && !isNaN(+discount_percentage)) {
    updateSessionState("browser", {
      freeMonthsOfferConfiguration: {
        shorterPlanID: shorter_plan_id,
        longerPlanID: noom_plan_id,
        discountPercentage: +discount_percentage,
      },
    });
  }
}

/**
 * Returns the configuration used for determining a free months offer
 * In case the configuration is not saved in session storage we load it from query params
 */
export function freeMonthsOfferConfiguration() {
  if (!isFreeMonthsOfferEligible(null)) {
    return null;
  }

  const configuration = getSessionState("browser").freeMonthsOfferConfiguration;
  if (configuration) {
    return configuration;
  }

  loadFreeMonthsOfferConfiguration();
  return getSessionState("browser").freeMonthsOfferConfiguration;
}

/**
 * A plan is eligible for the free months offer if the promo code discount percentage applied on it matches
 * the one defined in the query param discount_percentage. And his id matches shorter_plan_id from query params
 */
export function isPlanEligibleForFreeMonthsOffer(plan: Partial<Plan> | null) {
  const freeMonthsOffer = freeMonthsOfferConfiguration();

  return (
    isFreeMonthsOfferEligible() &&
    freeMonthsOffer &&
    freeMonthsOffer.discountPercentage ===
      plan?.discount_info?.discountPercent &&
    freeMonthsOffer.shorterPlanID === plan?.noom_plan_id
  );
}

/**
 * A free months offer gives the impression to user that it received free months when it applies a promo code.
 * It works by swapping a shorter plan with a longer one, and applies the promo code on the longer one,
 * so the price is the same or lower than the shorter plan. The feature is defined by 3 query params:
 * - shorter_plan_id Will be the plan forced on checkout
 * - discount_percentage For which promo code discount percentage applied on shorter plan we swap it with the longer one
 * - noom_plan_id Will be the longer plan
 * If applying the promo code on the longer plan fails we keep it applied on the first plan as fallback.
 * @param shorterPlan The shorter plan with promo code applied
 * @param promoCode The promo code applied on the shorter plan
 */
export function applyFreeMonthsOffer(
  shorterPlan: Partial<Plan> | null,
  promoCode: string
) {
  return async (dispatch: AppDispatch, getState: GetAppState) => {
    const { promoCode: promoCodeSlice } = getState();

    const task = trackTask("ApplyFreeMonthsOffer");
    try {
      task.start();
      // clear promo code applied on shorter plan, so we don't display it on UI
      await dispatch(clearPromoCode());

      const { longerPlanID } = freeMonthsOfferConfiguration();
      const longerPlan = await fetchProductPlanAsPlanById(longerPlanID);
      if (!longerPlan) {
        throw new Error(
          `No plan found with noom_plan_id for free months offer: ${longerPlanID}`
        );
      }
      const longerPlanDuration = longerPlan.billing_cycle_in_months;
      const freeMonths =
        longerPlanDuration - shorterPlan.billing_cycle_in_months;

      if (freeMonths <= 0) {
        throw new Error(
          `Invalid free months offer configuration freeMonths: ${freeMonths}`
        );
      }

      const longerPlanDiscounted = await dispatch(
        applyPromoCode(
          {
            ...longerPlan,
            // keep trial_fee from shorter plan
            trial_fee: shorterPlan.trial_fee,
          },
          promoCode,
          freeMonths
        )
      );

      if (!longerPlanDiscounted) {
        throw new Error(
          `Promo code ${promoCode} couldn't be applied for free months offer on plan ${longerPlanID}`
        );
      }
      await dispatch(
        setRecommendPlanById(longerPlanID, {
          overwriteForced: true,
          keepPromoCode: true,
        })
      );

      task.success();
      return longerPlanDiscounted;
    } catch (err) {
      captureException(err);
      task.failed(err);
      // restore the previous promo code
      dispatch(setPromoCode(promoCodeSlice));
      return shorterPlan;
    }
  };
}
